type KnownAnalyticsEventName = 'sign_up_completed' | 'first_report_created';

type AnalyticsEventName = KnownAnalyticsEventName | (string & {});

type AnalyticsEventPayload = Record<string, unknown>;

type DataLayerEvent = AnalyticsEventPayload & {
    event: AnalyticsEventName;
};

declare global {
    interface Window {
        dataLayer?: DataLayerEvent[];
    }
}

const SIGN_UP_STARTED_STORAGE_KEY = 'laudopsi:sign-up-started';
const FIRST_REPORT_CREATED_STORAGE_KEY_PREFIX = 'laudopsi:first-report-created';
const SUBSCRIPTION_CHECKOUT_STARTED_STORAGE_KEY = 'laudopsi:subscription-checkout-started';
const PAID_SIGNUP_INTENT_STORAGE_KEY = 'laudopsi:paid-signup-intent';

const getFirstReportCreatedStorageKey = (userId: string) =>
    `${FIRST_REPORT_CREATED_STORAGE_KEY_PREFIX}:${userId}`;

const trackAnalyticsEvent = (event: AnalyticsEventName, payload: AnalyticsEventPayload = {}) => {
    if (typeof window === 'undefined') return;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
        ...payload,
        event,
    });
};

export const trackSignUpCompleted = () => {
    trackAnalyticsEvent('sign_up_completed');
};

export const trackFirstReportCreated = () => {
    trackAnalyticsEvent('first_report_created');
};

export const markSignUpStarted = () => {
    if (typeof window === 'undefined') return;

    try {
        window.sessionStorage.setItem(SIGN_UP_STARTED_STORAGE_KEY, '1');
    } catch {
        // Storage can be unavailable in private or restricted browser contexts.
    }
};

export const consumeSignUpStarted = () => {
    if (typeof window === 'undefined') return false;

    try {
        const wasStarted = window.sessionStorage.getItem(SIGN_UP_STARTED_STORAGE_KEY) === '1';
        window.sessionStorage.removeItem(SIGN_UP_STARTED_STORAGE_KEY);

        return wasStarted;
    } catch {
        return false;
    }
};

export const hasTrackedFirstReportCreated = (userId?: string | null) => {
    if (!userId || typeof window === 'undefined') return false;

    try {
        return window.localStorage.getItem(getFirstReportCreatedStorageKey(userId)) === '1';
    } catch {
        return false;
    }
};

export const markFirstReportCreatedTracked = (userId?: string | null) => {
    if (!userId || typeof window === 'undefined') return;

    try {
        window.localStorage.setItem(getFirstReportCreatedStorageKey(userId), '1');
    } catch {
        // Storage can be unavailable in private or restricted browser contexts.
    }
};

/**
 * A note that this browser is the one that left for Stripe, so the confirmation
 * screen knows whether to wait for the subscription to appear.
 *
 * It used to carry the plan's price too, because the browser emitted the
 * conversion and nothing on the way back knew what the plan cost. The backend
 * emits it now, from the webhook, where the real amount charged is available.
 */
type SubscriptionCheckoutIntent = {
    hadSubscription: boolean;
};

export const markSubscriptionCheckoutStarted = ({
    hadSubscription,
}: SubscriptionCheckoutIntent) => {
    if (typeof window === 'undefined') return;

    try {
        window.sessionStorage.setItem(
            SUBSCRIPTION_CHECKOUT_STARTED_STORAGE_KEY,
            JSON.stringify({ hadSubscription }),
        );
    } catch {
        // Storage can be unavailable in private or restricted browser contexts.
    }
};

export const getSubscriptionCheckoutStarted = (): SubscriptionCheckoutIntent | null => {
    if (typeof window === 'undefined') return null;

    try {
        const rawValue = window.sessionStorage.getItem(SUBSCRIPTION_CHECKOUT_STARTED_STORAGE_KEY);
        if (!rawValue) return null;

        const parsed = JSON.parse(rawValue) as { hadSubscription?: unknown };

        return { hadSubscription: parsed.hadSubscription === true };
    } catch {
        return null;
    }
};

export const clearSubscriptionCheckoutStarted = () => {
    if (typeof window === 'undefined') return;

    try {
        window.sessionStorage.removeItem(SUBSCRIPTION_CHECKOUT_STARTED_STORAGE_KEY);
    } catch {
        // Storage can be unavailable in private or restricted browser contexts.
    }
};

export const markPaidSignupIntent = (planId: string) => {
    if (typeof window === 'undefined') return;

    try {
        window.sessionStorage.setItem(PAID_SIGNUP_INTENT_STORAGE_KEY, planId);
    } catch {
        // Storage can be unavailable in private or restricted browser contexts.
    }
};

export const clearPaidSignupIntent = () => {
    if (typeof window === 'undefined') return;

    try {
        window.sessionStorage.removeItem(PAID_SIGNUP_INTENT_STORAGE_KEY);
    } catch {
        // Storage can be unavailable in private or restricted browser contexts.
    }
};
